import type { FaqItem } from '../types/faq';
import type { Provider } from '../types/provider';
import type { Slot } from '../types/slot';
import { formatRtp, getCasinosForSlot } from './slots';
import { CONTACT_EMAIL } from '../data/contact';

export const SITE_URL = 'https://slotgids.co.nz';
export const SITE_NAME = 'SlotGids';
export const LAST_UPDATED = '2026-10-06';
export const YEAR = 2026;

type JsonLd = Record<string, unknown>;

export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path}`;
}

const publisher = { '@id': `${SITE_URL}/#organization` };

export function organizationLd(): JsonLd {
  return {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    description: 'Onafhankelijke Nederlandstalige gids voor online slots in Nieuw-Zeeland.',
    areaServed: { '@type': 'Country', name: 'New Zealand' },
    email: CONTACT_EMAIL,
    contactPoint: {
      '@type': 'ContactPoint',
      email: CONTACT_EMAIL,
      contactType: 'customer support',
      availableLanguage: ['nl', 'en'],
    },
  };
}

export function websiteLd(): JsonLd {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    inLanguage: 'nl-NZ',
    publisher,
  };
}

export function breadcrumbLd(items: { name: string; path: string }[]): JsonLd {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function slotItemListLd(list: Slot[], name: string): JsonLd {
  return {
    '@type': 'ItemList',
    name,
    numberOfItems: list.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: list.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: s.name,
      url: absoluteUrl(`/slots/${s.slug}`),
    })),
  };
}

export function faqLd(items: FaqItem[]): JsonLd {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

export function slotReviewLd(slot: Slot): JsonLd {
  const url = absoluteUrl(`/slots/${slot.slug}`);
  return {
    '@type': 'Review',
    '@id': `${url}#review`,
    name: `${slot.name} review voor Nieuw-Zeeland`,
    url,
    inLanguage: 'nl-NZ',
    dateModified: LAST_UPDATED,
    author: publisher,
    publisher,
    reviewBody: slot.verdict,
    reviewRating: { '@type': 'Rating', ratingValue: slot.score, bestRating: 10, worstRating: 0 },
    itemReviewed: {
      '@type': 'Game',
      name: slot.name,
      url,
      datePublished: String(slot.release),
      author: { '@type': 'Organization', name: slot.provider },
    },
  };
}

export function providerPageLd(provider: Provider, list: Slot[]): JsonLd {
  return {
    '@type': 'CollectionPage',
    '@id': absoluteUrl(`/providers/${provider.slug}#page`),
    name: `${provider.name} slots in Nieuw-Zeeland`,
    url: absoluteUrl(`/providers/${provider.slug}`),
    inLanguage: 'nl-NZ',
    dateModified: LAST_UPDATED,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: {
      '@type': 'Organization',
      name: provider.name,
      foundingDate: String(provider.founded),
      address: provider.headquarters,
    },
    mainEntity: slotItemListLd(list, `${provider.name} slots in de SlotGids Top 30`),
  };
}

export function getSlotFaq(slot: Slot): FaqItem[] {
  const casinoCount = getCasinosForSlot(slot).length;
  return [
    {
      question: `Wat is de RTP van ${slot.name}?`,
      answer: `De standaard-RTP van ${slot.name} is ${formatRtp(slot.rtp)}. Sommige casino’s bieden een versie met een lagere RTP aan; controleer dit in het informatiescherm van de slot.`,
    },
    {
      question: `Kan ik ${slot.name} gratis spelen?`,
      answer: `Ja. Op deze pagina kun je de ${slot.name} demo met speelgeld spelen, zonder account en zonder storting.`,
    },
    {
      question: `Is ${slot.name} beschikbaar in Nieuw-Zeeland?`,
      answer: `Ja, ${slot.provider} is actief in Nieuw-Zeeland en ${slot.name} is ${
        casinoCount > 0 ? `bij ${casinoCount} van onze partnercasino’s` : 'bij diverse casino’s'
      } in NZ$ te spelen. Speel alleen bij een casino met een geldige vergunning voor Nieuw-Zeeland.`,
    },
    {
      question: `Wat is de maximale winst bij ${slot.name}?`,
      answer:
        slot.maxWin === 'Progressief'
          ? `${slot.name} heeft progressieve jackpots; de maximale winst hangt af van de actuele jackpotstand.`
          : `De maximale winst bij ${slot.name} is ${slot.maxWin} je inzet.`,
    },
    {
      question: `Is ${slot.name} geschikt voor beginners?`,
      answer:
        slot.volatility === 'Laag' || slot.volatility === 'Gemiddeld'
          ? `Ja. Met een ${slot.volatility.toLowerCase()} volatiliteit geeft ${slot.name} regelmatig kleinere winsten, wat prettig is voor beginners.`
          : `${slot.name} heeft een ${slot.volatility.toLowerCase()} volatiliteit, wat betekent dat winsten minder vaak vallen maar groter kunnen zijn. Probeer eerst de demo en speel met een vast budget.`,
    },
  ];
}
